import React from 'react';
import { ScrollText, Shield, Users, Target, Heart, Sparkles, Award, Handshake, Lightbulb, Crown } from 'lucide-react';

export default function About() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-gold/10 border border-brand-gold/20 mb-6">
          <ScrollText size={18} className="text-brand-gold" />
          <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider">Our Manifesto</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold gradient-text mb-4">Economic Club of San Antonio</h1>
        <p className="text-xl text-gray-400">Our principles, our promise, our purpose</p>
      </div>

      {/* Introduction */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4 flex items-center gap-3">
          <Sparkles size={24} />
          Introduction
        </h2>
        <p className="text-gray-300 leading-relaxed">
          Economic Club of San Antonio stands as a bastion of economic stability, an advocate for the upholding of sound business principles, and a champion for the greater good of society above individual self-service. Rooted in these foundational principles, we embark on a mission to cultivate a community of like-minded individuals dedicated to the advancement of economic understanding and the promotion of ethical business practices.
        </p>
      </div>

      {/* Economic Stability */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4 flex items-center gap-3">
          <Shield size={24} />
          Economic Stability
        </h2>
        <p className="text-gray-300 leading-relaxed">
          We recognize the pivotal role of economic stability in fostering prosperity and progress within society. As stewards of economic knowledge, we strive to deepen our understanding of economic principles and advocate for policies that promote stability, sustainable growth, and equitable distribution of resources. Through rigorous analysis and informed discourse, we seek to contribute to the creation of resilient economic systems that withstand challenges and serve the needs of all members of society.
        </p>
      </div>

      {/* Upholding Business Principles */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4 flex items-center gap-3">
          <Award size={24} />
          Upholding Business Principles
        </h2>
        <p className="text-gray-300 leading-relaxed">
          At Economic Club of San Antonio, we hold ourselves to the highest standards of integrity, honesty, and accountability in all our business endeavors. We believe that ethical conduct is not merely a choice, but a fundamental obligation to society. In our interactions with stakeholders, colleagues, and competitors, we pledge to uphold the principles of fairness, transparency, and respect. We reject unethical practices such as fraud, corruption, and exploitation, recognizing that they undermine trust, impede progress, and erode the foundations of a just society.
        </p>
      </div>

      {/* Respect for the Greater Good */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4 flex items-center gap-3">
          <Heart size={24} />
          Respect for the Greater Good
        </h2>
        <p className="text-gray-300 leading-relaxed">
          Central to our ethos is the recognition that the pursuit of individual interests must be balanced with a commitment to the welfare of society as a whole. We believe that true prosperity arises not from the accumulation of wealth and power, but from the collective well-being of all members of society. As responsible citizens and business leaders, we strive to leverage our knowledge, resources, and influence for the betterment of our communities and the advancement of common goals. We reject narrow self-interest in favor of a broader vision that prioritizes social responsibility, environmental sustainability, and the promotion of human dignity.
        </p>
      </div>

      {/* Guiding Principles */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-6 flex items-center gap-3">
          <Target size={24} />
          Guiding Principles
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
            <div className="flex items-center gap-3 mb-2">
              <Shield size={20} className="text-brand-gold" />
              <h3 className="font-bold text-white">Integrity</h3>
            </div>
            <p className="text-gray-400 text-sm">We conduct ourselves with integrity, acting in accordance with ethical principles and moral values.</p>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
            <div className="flex items-center gap-3 mb-2">
              <Award size={20} className="text-brand-gold" />
              <h3 className="font-bold text-white">Excellence</h3>
            </div>
            <p className="text-gray-400 text-sm">We pursue excellence in all aspects of our endeavors, striving for continuous improvement and innovation.</p>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
            <div className="flex items-center gap-3 mb-2">
              <Handshake size={20} className="text-brand-gold" />
              <h3 className="font-bold text-white">Collaboration</h3>
            </div>
            <p className="text-gray-400 text-sm">We foster a culture of collaboration and mutual respect, recognizing the collective wisdom and strength of diverse perspectives.</p>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
            <div className="flex items-center gap-3 mb-2">
              <Lightbulb size={20} className="text-brand-gold" />
              <h3 className="font-bold text-white">Empowerment</h3>
            </div>
            <p className="text-gray-400 text-sm">We empower individuals to realize their full potential, promoting education, opportunity, and inclusivity.</p>
          </div>
          <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10 sm:col-span-2">
            <div className="flex items-center gap-3 mb-2">
              <Crown size={20} className="text-brand-gold" />
              <h3 className="font-bold text-white">Leadership</h3>
            </div>
            <p className="text-gray-400 text-sm">We lead by example, inspiring others through our actions, integrity, and commitment to the common good.</p>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="glass-card rounded-2xl p-8 mb-8 bg-gradient-to-r from-brand-gold/10 to-transparent border-brand-gold/30">
        <h2 className="text-2xl font-bold text-brand-gold mb-4 flex items-center gap-3">
          <Users size={24} />
          Call to Action
        </h2>
        <p className="text-gray-300 leading-relaxed">
          We invite all individuals who share our vision and values to join us in this noble endeavor. Together, let us harness the power of economics to build a more just, prosperous, and sustainable world for present and future generations.
        </p>
      </div>

      {/* Upholding Ethical Business Practices */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4">Upholding Ethical Business Practices</h2>
        <p className="text-gray-300 leading-relaxed">
          We are steadfast in our commitment to upholding the highest standards of ethical business conduct. Economic Club of San Antonio stands resolutely against unethical practices, including fraud, deception, and exploitation. We believe that integrity is the cornerstone of sustainable business success and that trust is earned through honesty, transparency, and accountability. Members of Economic Club of San Antonio pledge to conduct themselves with integrity in all business dealings, treating their colleagues, clients, and competitors with fairness, respect, and honesty.
        </p>
      </div>

      {/* Rejection of Unethical Business Practices */}
      <div className="glass-card rounded-2xl p-8 mb-8">
        <h2 className="text-2xl font-bold text-brand-gold mb-4">Rejection of Unethical Business Practices</h2>
        <p className="text-gray-300 leading-relaxed">
          Economic Club of San Antonio unequivocally rejects any form of defrauding or unethical business practices. Members found engaging in such misconduct will be subject to investigation and disciplinary action, including potential dismissal from the club. We are committed to fostering a culture of integrity, where ethical behavior is not only expected but celebrated as a hallmark of professional excellence. By adhering to the highest ethical standards, we strive to earn the trust and respect of our peers, clients, and the broader community.
        </p>
      </div>

      {/* Conclusion */}
      <div className="glass-card rounded-2xl p-8 mb-8 bg-gradient-to-r from-brand-gold/5 to-transparent">
        <h2 className="text-2xl font-bold text-brand-gold mb-4">Conclusion</h2>
        <p className="text-gray-300 leading-relaxed mb-6">
          In Economic Club of San Antonio, we stand united in our commitment to economic stability, the upholding of business principles, and the pursuit of the greater good. Through our collective efforts and unwavering dedication, we aspire to be agents of positive change, shaping a future where prosperity is equitably shared, and the dignity of all individuals is respected and upheld. We invite all individuals who share our vision and values to join us in this noble endeavor. Together, let us harness the power of economics to build a more just, prosperous, and sustainable world for present and future generations.
        </p>
        <div className="pt-6 border-t border-brand-gold/20">
          <p className="text-brand-gold font-semibold italic">Signed,</p>
          <p className="text-white font-bold text-lg mt-2">Founders of Economic Club of San Antonio</p>
        </div>
      </div>
    </div>
  );
}
